import { useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { trackBillingAddressStarted } from "@/features/analytics/trackers/billing-address-started";
import { trackCompletePurchaseClicked } from "@/features/analytics/trackers/complete-purchase-clicked";
import { trackPaymentMethodSelected } from "@/features/analytics/trackers/payment-method-selected";
import type { IntakeFormPayload } from "@/features/billing/api";
import { PayButton, PaymentErrorAlert } from "@/features/onboarding/components";
import {
  useCheckout,
  useCompleteOnboarding,
  useCreateUserForm,
  useHandlePaymentRedirect,
  useOnboardingUser,
  useTrackCheckoutInitiated,
} from "@/features/onboarding/hooks";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import {
  AddressFieldset,
  PaymentFieldset,
} from "@/features/onboarding/sections";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { MissingClientSecretError } from "@/features/payments/utils";
import { ProviderCallModal } from "@/features/questionnaire-onboarding/components";
import { LogoLoader } from "@/shared/components";
import type { Price } from "@/shared/domains";
import { PAYMENT_METHOD_TYPE, SUBSCRIPTION_VENDOR } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import {
  Checkbox,
  Collapse,
  errorToast,
  Form,
  validateError,
} from "@/shared/ui";
import { formatPrice } from "@/shared/utils";
import {
  FunnelPaymentMethodSelector,
  FunnelTimerBanner,
  FunnelTrustpilot,
  useCountdown,
} from "../components";
import { FUNNEL_ROUTE } from "../router/constants";
import { useFunnelStore } from "../stores";
import { funnelCheckoutIntake } from "../utils";

const PAYMENT_FORM_ID = "funnel-payment-form";

const RESERVED_SECONDS = 574;

const effectivePrice = (price: Price) =>
  price.discount?.isValid ? price.discount.priceAfterDiscount : price.price;

const SummaryRow = ({
  label,
  value,
  bold = false,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className={bold ? "font-bold text-neutral-900" : "text-neutral-700"}>
      {label}
    </span>
    <span className="font-bold text-neutral-900">{value}</span>
  </div>
);

export const Payment = () => {
  const navigate = useNavigate();
  const time = useCountdown(RESERVED_SECONDS);

  const { value: showPaymentFailedAlert, setTrue: openPaymentFailedAlert } =
    useBoolean(false);
  const { value: showProviderCallModal, setTrue: openProviderCallModal } =
    useBoolean(false);

  const billingAddressTracked = useRef(false);

  const goToAccountCreation = () => navigate(ONBOARDING_ROUTE.ACCOUNT_PASSWORD);

  const contactDetails = useOnboardingStore((state) => state.contactDetails);
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const setContactDetails = useOnboardingStore(
    (state) => state.setContactDetails,
  );
  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const intakeAnswers = useFunnelStore((store) => store.intakeAnswers);
  const intakeForm = useMemo<IntakeFormPayload | undefined>(
    () => funnelCheckoutIntake({ intakeAnswers }),
    [intakeAnswers],
  );

  const user = useOnboardingUser();
  const form = useCreateUserForm({ requireTermsAndConditionsConsent: false });
  const isBillingSameAsShipping = form.watch("isBillingSameAsShipping");
  const paymentMethodType = form.watch("paymentMethodType");
  const isKlarna = paymentMethodType === PAYMENT_METHOD_TYPE.KLARNA;

  const { isVerifying, isRedirect } = useHandlePaymentRedirect({
    formId: PAYMENT_FORM_ID,
    eventPrefix: EVENT_PREFIX.FUNNEL,
    intakeForm,
    onNeedsWhiteCoatSync: openProviderCallModal,
  });

  const {
    card: { pay: cardPay },
    klarna: { pay: klarnaPay },
  } = useCheckout({
    formId: PAYMENT_FORM_ID,
    klarnaReturnPath: FUNNEL_ROUTE.PAYMENT,
    intakeForm,
    eventPrefix: EVENT_PREFIX.FUNNEL,
  });

  const completeOnboardingMutation = useCompleteOnboarding({
    onNeedsWhiteCoatSync: openProviderCallModal,
  });

  const cardPayMutation = useMutation({
    mutationFn: cardPay,
    onSuccess: (data) => {
      if (data?.needsSync && data.vendor === SUBSCRIPTION_VENDOR.WHITE_COAT) {
        openProviderCallModal();
        return;
      }
      goToAccountCreation();
    },
    onError: (error) => {
      const validatedError = validateError(error);
      if (
        validatedError &&
        validatedError.response.data.error.code !== "stripe_card_error"
      ) {
        openPaymentFailedAlert();
      }
    },
  });

  const klarnaPayMutation = useMutation({
    mutationFn: klarnaPay,
    onError: (error) => {
      if (error instanceof MissingClientSecretError) {
        if (!user) {
          return;
        }
        return completeOnboardingMutation.mutate({
          code: user.code,
          state: user.state,
          shipping: user.shipping,
          phoneNumber: user.phoneNumber,
          isCallCenter,
          ...(intakeForm && { intakeForm }),
        });
      }
      errorToast(error.message);
    },
  });

  useTrackCheckoutInitiated({
    enabled: !isRedirect,
    eventPrefix: EVENT_PREFIX.FUNNEL,
  });

  if (!intakeForm) {
    return <Navigate to={FUNNEL_ROUTE.PERSONAL_INFO} replace />;
  }

  if (!user || !selectedCategories.length) {
    return <Navigate to={FUNNEL_ROUTE.PRICING} replace />;
  }

  const selectedProduct = selectedCategories[0]?.selectedProduct;
  const selectedPrice = selectedProduct?.selectedPrice;
  const total = selectedPrice ? effectivePrice(selectedPrice) : 0;
  const monthly = selectedPrice ? total / selectedPrice.durationInMonths : 0;
  // Kept as a plain variable so it can be interpolated in a ttag string.
  const planDurationMonths = selectedPrice?.durationInMonths ?? 0;

  const handlePay = form.handleSubmit((formData) => {
    if (contactDetails) {
      setContactDetails({
        ...contactDetails,
        shipping: formData.shipping,
        billing: formData.billing ?? formData.shipping,
        state: formData.state,
      });
    }

    if (formData.paymentMethodType === PAYMENT_METHOD_TYPE.CARD) {
      trackCompletePurchaseClicked({
        user,
        selectedCategories: useOnboardingStore.getState().selectedCategories,
        paymentMethod: formData.paymentMethodType,
        eventPrefix: EVENT_PREFIX.FUNNEL,
      });
      return cardPayMutation.mutate();
    }

    if (formData.paymentMethodType === PAYMENT_METHOD_TYPE.KLARNA) {
      return klarnaPayMutation.mutate();
    }
  });

  const handleSelectMethod = (method: "card" | "bnpl") => {
    // Selecting a tile only switches the method. Submitting/redirecting is the
    // job of the explicit pay button (whose label switches to "Pay with Klarna"),
    // so choosing BNPL doesn't kick off a Klarna redirect or splash validation
    // errors across the address fieldset.
    const paymentMethodType =
      method === "card" ? PAYMENT_METHOD_TYPE.CARD : PAYMENT_METHOD_TYPE.KLARNA;

    form.setValue("paymentMethodType", paymentMethodType);

    trackPaymentMethodSelected({
      paymentMethod: paymentMethodType,
      userId: user.email,
      eventPrefix: EVENT_PREFIX.FUNNEL,
    });
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-5 py-8">
      <h1 className="text-center text-3xl font-extrabold text-neutral-900 sm:text-4xl">
        {t`Choose Payment Method`}
      </h1>
      <p className="mt-2 text-center text-base text-neutral-600">
        {t`Your data is protected. All transactions are secured and encrypted.`}
      </p>

      <div className="mt-6">
        <FunnelPaymentMethodSelector
          value={isKlarna ? "bnpl" : "card"}
          onSelect={handleSelectMethod}
        />
      </div>

      <Form.Provider {...form}>
        <form
          id={PAYMENT_FORM_ID}
          onSubmit={handlePay}
          className="mt-8 flex flex-col gap-8"
        >
          <fieldset
            className="flex flex-col gap-4"
            onFocus={() => {
              if (billingAddressTracked.current) {
                return;
              }
              billingAddressTracked.current = true;
              trackBillingAddressStarted({
                userId: user.email,
                eventPrefix: EVENT_PREFIX.FUNNEL,
              });
            }}
          >
            <h2 className="text-xl font-bold text-neutral-900">
              {t`Shipping Address`}
            </h2>

            <AddressFieldset form={form} type="shipping" />

            <Form.Field
              control={form.control}
              name="isBillingSameAsShipping"
              render={({ field: { value, onChange, ...rest } }) => (
                <Form.Item id="billing-same-as-shipping">
                  <Form.Control>
                    <Checkbox.Item
                      checked={value}
                      onCheckedChange={(checked) => {
                        onChange(checked);
                        form.clearErrors("billing");
                        form.setValue("billing", undefined);
                      }}
                      {...rest}
                    >
                      <Checkbox.Label>
                        {t`Billing address is the same as shipping`}
                      </Checkbox.Label>
                    </Checkbox.Item>
                  </Form.Control>
                </Form.Item>
              )}
            />

            <AnimatePresence>
              {!isBillingSameAsShipping && (
                <Collapse key="billing-address" className="col-span-full">
                  <AddressFieldset
                    form={form}
                    type="billing"
                    title={t`Billing address`}
                  />
                </Collapse>
              )}
            </AnimatePresence>
          </fieldset>

          {!isKlarna && (
            <PaymentFieldset
              headingAlign="left"
              onClick={handlePay}
              eventPrefix={EVENT_PREFIX.FUNNEL}
              walletsFirst
            />
          )}

          {selectedProduct && (
            <div className="rounded-2xl bg-secondary-50 p-6">
              <FunnelTrustpilot />
              <h3 className="mt-4 text-center text-2xl font-extrabold text-neutral-900">
                {t`Your Treatment Details`}
              </h3>

              <div className="mt-4 rounded-xl bg-white p-5">
                <SummaryRow
                  label={t`Medication:`}
                  value={selectedProduct.name}
                  bold
                />
                {selectedPrice && (
                  <SummaryRow
                    label={t`Delivery Plan:`}
                    value={
                      planDurationMonths > 1
                        ? t`${planDurationMonths}-Month plan`
                        : t`Monthly plan`
                    }
                    bold
                  />
                )}
                <SummaryRow label={t`Shipping:`} value={t`FREE`} bold />
                <SummaryRow
                  label={t`Online Doctor Review:`}
                  value={t`Included`}
                  bold
                />
                <SummaryRow
                  label={t`24/7 Clinical Chat Support:`}
                  value={t`Included`}
                  bold
                />
                <SummaryRow
                  label={t`Overnight Cold Shipping:`}
                  value={t`Included`}
                  bold
                />
                <SummaryRow label={t`All Doses:`} value={t`Included`} bold />
                <SummaryRow
                  label={t`Free Dietitian Consultation:`}
                  value={t`Included`}
                  bold
                />
                <SummaryRow
                  label={t`Complimentary Menu Options:`}
                  value={t`Included`}
                  bold
                />

                <hr className="my-2 border-secondary-200" />

                <SummaryRow
                  label={t`Monthly Price:`}
                  value={formatPrice(monthly)}
                  bold
                />
                <SummaryRow label={t`Due Today:`} value={formatPrice(0)} bold />
                <SummaryRow
                  label={t`Total if prescribed:`}
                  value={formatPrice(total)}
                  bold
                />
              </div>

              <p className="mt-4 text-center font-bold text-neutral-900">
                {t`Your Discount is Reserved for`}{" "}
                <span className="text-secondary-500">{time}</span>
              </p>
            </div>
          )}

          <FunnelTimerBanner time={time} />

          <div className="flex flex-col items-center gap-3">
            <PayButton
              label={isKlarna ? t`Pay with Klarna` : t`Continue`}
              disabled={
                cardPayMutation.isPending || klarnaPayMutation.isPending
              }
            />
          </div>

          <div className="rounded-xl bg-green-500 py-5 text-center text-white">
            <p className="text-xl font-extrabold">{t`$0 Due Today!`}</p>
            <p className="text-sm">{t`Only charged if your prescription is approved.`}</p>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="font-bold text-neutral-900">{t`$0 Due Today`}</p>
              <p className="text-xs text-neutral-600">
                {t`Only charged if your prescription is approved.`}
              </p>
            </div>
            <div>
              <p className="font-bold text-neutral-900">
                {t`Prescribed & shipped within 48 hours`}
              </p>
            </div>
            <div>
              <p className="font-bold text-neutral-900">
                {t`UNLIMITED doctor calls 7 days a week`}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white py-4 text-center">
            <span className="font-bold text-neutral-900">
              <span className="text-green-600">✓</span> {t`HSA/FSA Eligible`}
            </span>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-5 text-center">
            <p className="text-secondary-400">★★★★★</p>
            <p className="mt-2 text-neutral-700 italic">
              {t`"Been using them since December and it's been a great experience, they are super helpful every time I needed anything."`}
            </p>
            <p className="mt-1 font-semibold text-neutral-900">{t`– Scott B.`}</p>
          </div>

          <p className="text-center text-sm font-semibold text-neutral-700">
            🔒 {t`Guaranteed safe & secure checkout`}
          </p>
        </form>
      </Form.Provider>

      <LogoLoader
        show={
          cardPayMutation.isPending ||
          completeOnboardingMutation.isPending ||
          isVerifying
        }
        fullscreen
      >
        {t`We are processing your payment, just give us a moment.`}
      </LogoLoader>

      <LogoLoader show={klarnaPayMutation.isPending} fullscreen>
        {t`Almost there — we're redirecting you to Klarna to complete your payment`}
      </LogoLoader>

      <PaymentErrorAlert open={showPaymentFailedAlert} />

      <ProviderCallModal
        open={showProviderCallModal}
        onContinue={goToAccountCreation}
      />
    </div>
  );
};
